import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useLang } from "../contexts/LangContext";
import { api, formatApiError } from "../lib/api";
import Logo from "../components/Logo";
import { toast } from "sonner";
import { Lock, ArrowLeft } from "lucide-react";

export default function ResetPassword() {
  const { t } = useLang();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (password.length < 6) {
      setErr("Password must be at least 6 characters");
      toast.error("Password must be at least 6 characters");
      return;
    }
    if (password !== confirm) {
      setErr(t("passwordMismatch"));
      toast.error(t("passwordMismatch"));
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      toast.success(t("resetSuccess"));
      nav("/login");
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  if (!token) {
    return (
      <main className="flex min-h-[calc(100vh-72px)] items-center justify-center px-4 py-16">
        <div className="w-full max-w-md card p-8 text-center">
          <div className="font-display text-2xl">Invalid link</div>
          <Link to="/forgot-password" className="btn btn-primary mt-4">
            {t("forgotTitle")}
          </Link>
        </div>
      </main>
    );
  }

  return (
    <main className="flex min-h-[calc(100vh-72px)] items-center justify-center px-4 py-16">
      <div className="w-full max-w-md card p-8 reveal" data-testid="reset-card">
        <Logo size={44} color="hsl(var(--ink))" className="mb-6" />
        <Link to="/login" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-muted-fg hover:text-ink">
          <ArrowLeft className="h-3.5 w-3.5 rtl:rotate-180" />
          {t("signIn")}
        </Link>
        <h1 className="font-display mt-4 text-4xl">
          {t("resetTitle")}
        </h1>
        <p className="mt-3 text-sm text-muted-fg">{t("resetSub")}</p>

        <form onSubmit={submit} className="mt-6 space-y-4">
          <div>
            <label className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-fg">
              {t("newPassword")}
            </label>
            <div className="relative mt-1.5">
              <Lock className="absolute start-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-fg" />
              <input
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="input-base ps-10"
                data-testid="reset-password"
              />
            </div>
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-fg">
              {t("confirmPassword")}
            </label>
            <div className="relative mt-1.5">
              <Lock className="absolute start-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-fg" />
              <input
                type="password"
                required
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                className="input-base ps-10"
                data-testid="reset-confirm"
              />
            </div>
          </div>

          {err && <div className="rounded-lg bg-red-50 p-3 text-sm text-red-700 dark:bg-red-950/50 dark:text-red-300" data-testid="reset-error">{err}</div>}

          <button type="submit" disabled={busy} className="btn btn-accent w-full" data-testid="reset-submit">
            {busy ? "…" : t("resetSubmit")}
          </button>
        </form>
      </div>
    </main>
  );
}
